import React from 'react';
import { Heart, Shield, Users, Zap } from 'lucide-react';

export const About = () => {
  const values = [
    {
      icon: Heart,
      title: "Built with Care",
      description: "We believe connection should feel natural, not invasive. Every feature is designed with empathy and intention."
    },
    {
      icon: Shield,
      title: "Privacy First",
      description: "Your location data belongs to you. We never sell or share your information with third parties."
    },
    {
      icon: Users,
      title: "Community Driven",
      description: "Fuzz is built for real people living real lives. Your feedback shapes every update we make."
    },
    {
      icon: Zap,
      title: "Simple & Smart",
      description: "Powerful features that work seamlessly in the background. Technology that stays out of your way."
    }
  ];

  return (
    <section id="about" className="py-24 bg-white">
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              About Fuzz
            </h2>
            <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              We're reimagining how people stay connected in the real world. Fuzz combines social proximity with trusted safety features — bringing closeness, comfort, and peace of mind into one simple app.
            </p>
          </div>

          {/* Mission Statement */}
          <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-3xl p-8 md:p-12 mb-16 border border-teal-100">
            <div className="max-w-3xl mx-auto text-center">
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                Our Mission
              </h3>
              <p className="text-lg text-gray-700 leading-relaxed">
                In a world where we're more "connected" than ever but feel further apart, Fuzz brings the magic of serendipity back to your everyday life. We help you discover when friends are nearby, keep your loved ones safe, and create real-world moments that matter — without being creepy or intrusive.
              </p>
            </div>
          </div>

          {/* Values Grid */}
          <div className="grid md:grid-cols-2 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div 
                  key={index}
                  className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-2xl border border-gray-200 hover:shadow-xl transition-all hover:-translate-y-1"
                >
                  <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-2xl flex items-center justify-center mb-6">
                    <Icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    {value.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {value.description}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Story */}
          <div className="mt-16 text-center">
            <div className="max-w-3xl mx-auto">
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
                Why We Built Fuzz
              </h3>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                We've all had those moments — scrolling through social media only to realize a close friend was in the same neighborhood. Or worrying about a loved one's safety but not wanting to constantly check in. Or missing out on spontaneous meetups because no one knew you were nearby.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Fuzz solves these everyday moments with elegance and respect. Because life's best connections happen when you're together — and sometimes, all you need is a gentle nudge to make it happen.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
